import { esAsignacionFamiliarPlame } from './conceptosEstaticosPlanilla';
import {
  CODIGO_DESCUENTO_DOMINICAL_JORNAL,
  CODIGO_DESCUENTO_TARDANZA_JORNAL,
  esDescuentoDominicalJornal,
  esDescuentoTardanzaJornal,
  normalizarTipoEvento,
  redondear2,
  toYmd,
} from './pagoJornalPlanilla';

const CODIGOS_DESCUENTO_MENSUAL = new Set(['0705']);
const CODIGOS_DESCUENTO_JORNAL = new Set([
  CODIGO_DESCUENTO_DOMINICAL_JORNAL,
  CODIGO_DESCUENTO_TARDANZA_JORNAL,
]);

export function esMixtoParaBoletaPdf(empleado) {
  const meta = empleado?.pagoJornal;
  return Boolean(
    meta?.aplica
    && String(meta.modo || '').toLowerCase() === 'mixto'
    && meta.rangoMensual?.inicio
    && meta.rangoMensual?.fin
    && meta.rangoJornal?.inicio
    && meta.rangoJornal?.fin
  );
}

export function fechaEnRangoMixto(fecha, rango) {
  const f = toYmd(fecha);
  const a = toYmd(rango?.inicio);
  const b = toYmd(rango?.fin);
  return Boolean(f && a && b && f >= a && f <= b);
}

export function normalizarCodigoMixto(codigo) {
  const s = String(codigo || '').trim();
  if (!s) return '';
  if (/^\d+$/.test(s)) return s.padStart(4, '0');
  return s.toUpperCase();
}

export function codigoConceptoBoleta(concepto) {
  return normalizarCodigoMixto(
    concepto?.codigoPlame
    || concepto?.codigo_plame
    || concepto?.codigo
    || concepto?.plame_configuracion?.codigo_plame
    || ''
  );
}

function partesDesdePesos(total, pesoA, pesoB) {
  const split = partirMontoPorPesos(total, pesoA, pesoB);
  return { mensual: split.a, jornal: split.b };
}

function normalizarPartes(partes, oficial, ctx) {
  const monto = redondear2(oficial);
  const mensual = redondear2(partes?.mensual ?? partes?.a ?? 0);
  const jornal = redondear2(partes?.jornal ?? partes?.b ?? 0);
  const suma = redondear2(mensual + jornal);
  if (monto <= 0) return { mensual: 0, jornal: 0 };
  if (suma <= 0) {
    return partesDesdePesos(
      monto,
      ctx?.ingresosMensual || ctx?.pesoMensual || 1,
      ctx?.ingresosJornal || ctx?.pesoJornal || 0
    );
  }
  if (suma === monto) return { mensual, jornal };
  return { mensual, jornal: redondear2(monto - mensual) };
}

function tareosDeEmpleado(empleado) {
  const candidatos = [
    empleado?.tareosEmpleado,
    empleado?.tareosEmpleadoFiltrados,
    empleado?.tareos?.tareosDetallados,
    Array.isArray(empleado?.tareos) ? empleado.tareos : null,
  ];
  const found = candidatos.find((lista) => Array.isArray(lista) && lista.length);
  return found || [];
}

function listarConceptosGrupo(empleado, tipo) {
  const grouped = empleado?.conceptos?.[tipo];
  if (Array.isArray(grouped) && grouped.length) return grouped;
  const sep = empleado?.conceptos_separados?.[tipo];
  if (Array.isArray(sep) && sep.length) return sep;
  const flat = Array.isArray(empleado?.conceptos) ? empleado.conceptos : [];
  const needle = tipo === 'ingresos' ? 'ingreso' : tipo === 'descuentos' ? 'descuento' : 'aporte';
  return flat.filter((c) => String(c.tipo_concepto || '').toLowerCase().includes(needle));
}

function montoConcepto(concepto) {
  return redondear2(concepto?.monto ?? concepto?.valor ?? 0);
}

function textoConcepto(concepto) {
  return String(concepto?.descripcion || concepto?.nombre || concepto?.concepto || '').toLowerCase();
}

function tareosEnRango(tareos, rango) {
  return (tareos || []).filter((t) => fechaEnRangoMixto(t.fecha || t.fecha_tareo, rango));
}

function codigoTareoConcepto(concepto) {
  return normalizarCodigoMixto(
    concepto?.plame_configuracion?.codigo_plame
    || concepto?.codigo_plame
    || concepto?.codigoPlame
    || concepto?.codigo
    || ''
  );
}

export function montoTareoPorCodigoEnRango(tareos, codigo, rango, tipos = ['ingreso', 'descuento', 'adelanto']) {
  const buscado = normalizarCodigoMixto(codigo);
  if (!buscado) return 0;
  let suma = 0;
  tareosEnRango(tareos, rango).forEach((tareo) => {
    (tareo.conceptos || []).forEach((c) => {
      const tb = String(c.tipo_boleta || 'plame').toLowerCase();
      if (tb === 'interna') return;
      const tipo = String(c.tipo_concepto || '').toLowerCase();
      if (!tipos.includes(tipo)) return;
      if (codigoTareoConcepto(c) !== buscado) return;
      suma += Number(c.monto) || 0;
    });
  });
  return redondear2(suma);
}

export function horasDeficitTardanzaEnRango(tareos, rango) {
  let horas = 0;
  tareosEnRango(tareos, rango).forEach((t) => {
    const tipo = normalizarTipoEvento(t.tipo_evento);
    if (!['asistio', 'asistencia_completa', 'tardanza'].includes(tipo)) return;
    const hechas = Number(t.horas_trabajadas || t.horas || 0);
    const teorica = Number(t.horas_teoricas || t.horas_programadas || 8) || 8;
    if (hechas < teorica) horas += teorica - hechas;
  });
  return redondear2(horas);
}

export function partirMontoPorPesos(total, pesoA, pesoB) {
  const monto = Number(total) || 0;
  const a = Number(pesoA) || 0;
  const b = Number(pesoB) || 0;
  if (monto <= 0) return { a: 0, b: 0 };
  if (a <= 0 && b <= 0) return { a: redondear2(monto), b: 0 };
  if (a <= 0) return { a: 0, b: redondear2(monto) };
  if (b <= 0) return { a: redondear2(monto), b: 0 };
  const parteA = redondear2(monto * (a / (a + b)));
  return { a: parteA, b: redondear2(monto - parteA) };
}

export function partir0121Mixto(meta, montoOficial) {
  const oficial = redondear2(montoOficial);
  const jornalEst = redondear2(
    (Number(meta?.jornal0121) || 0) * (Number(meta?.diasPagables) || 0)
  );
  if (jornalEst <= 0) return { mensual: oficial, jornal: 0 };
  if (jornalEst >= oficial) return { mensual: 0, jornal: oficial };
  return {
    mensual: redondear2(oficial - jornalEst),
    jornal: jornalEst,
  };
}

function clonarConcepto(concepto, monto) {
  const descripcion = concepto.descripcion || concepto.nombre || concepto.concepto || codigoConceptoBoleta(concepto);
  return {
    ...concepto,
    descripcion,
    nombre: concepto.nombre || descripcion,
    concepto: concepto.concepto || descripcion,
    codigo: codigoConceptoBoleta(concepto) || concepto.codigo,
    codigoPlame: codigoConceptoBoleta(concepto) || concepto.codigoPlame,
    codigo_plame: codigoConceptoBoleta(concepto) || concepto.codigo_plame,
    monto: redondear2(monto),
  };
}

function partirIngreso(concepto, ctx) {
  const codigo = codigoConceptoBoleta(concepto);
  const oficial = montoConcepto(concepto);
  if (oficial <= 0) return { mensual: 0, jornal: 0 };

  if (codigo === '0121') {
    return partir0121Mixto(ctx.meta, oficial);
  }
  if (codigo === '0118') {
    return { mensual: 0, jornal: oficial };
  }
  if (esAsignacionFamiliarPlame(codigo)) {
    return { mensual: oficial, jornal: 0 };
  }

  const mensualTareo = montoTareoPorCodigoEnRango(ctx.tareos, codigo, ctx.rangoMensual, ['ingreso']);
  const jornalTareo = montoTareoPorCodigoEnRango(ctx.tareos, codigo, ctx.rangoJornal, ['ingreso']);
  const tareoTotal = redondear2(mensualTareo + jornalTareo);

  if (tareoTotal > 0) {
    if (tareoTotal >= oficial) {
      return partesDesdePesos(oficial, mensualTareo, jornalTareo);
    }
    const planta = redondear2(oficial - tareoTotal);
    const plantaPartes = partesDesdePesos(planta, ctx.pesoMensual, ctx.pesoJornal);
    return {
      mensual: redondear2(mensualTareo + plantaPartes.mensual),
      jornal: redondear2(jornalTareo + plantaPartes.jornal),
    };
  }

  return partesDesdePesos(oficial, ctx.pesoMensual, ctx.pesoJornal);
}

function partirDescuento(concepto, ctx) {
  const codigo = codigoConceptoBoleta(concepto);
  const oficial = montoConcepto(concepto);
  if (oficial <= 0) return { mensual: 0, jornal: 0 };

  const texto = textoConcepto(concepto);
  if (
    concepto?.fusionarEn0705
    || CODIGOS_DESCUENTO_MENSUAL.has(codigo)
    || texto.includes('inasistencia')
  ) {
    return { mensual: oficial, jornal: 0 };
  }
  if (
    CODIGOS_DESCUENTO_JORNAL.has(codigo)
    || esDescuentoDominicalJornal(concepto)
    || esDescuentoTardanzaJornal(concepto)
    || (texto.includes('dominical') && !concepto?.fusionarEn0705)
  ) {
    return { mensual: 0, jornal: oficial };
  }
  if (codigo === '0704' || (texto.includes('tardanza') && !esDescuentoTardanzaJornal(concepto))) {
    if ((ctx.horasTardanzaMensual || 0) > 0 || (ctx.horasTardanzaJornal || 0) > 0) {
      return partesDesdePesos(oficial, ctx.horasTardanzaMensual, ctx.horasTardanzaJornal);
    }
    return partesDesdePesos(oficial, ctx.ingresosMensual, ctx.ingresosJornal);
  }

  const mensualTareo = montoTareoPorCodigoEnRango(
    ctx.tareos,
    codigo,
    ctx.rangoMensual,
    ['descuento', 'adelanto']
  );
  const jornalTareo = montoTareoPorCodigoEnRango(
    ctx.tareos,
    codigo,
    ctx.rangoJornal,
    ['descuento', 'adelanto']
  );
  const tareoTotal = redondear2(mensualTareo + jornalTareo);
  if (tareoTotal > 0) {
    return partesDesdePesos(oficial, mensualTareo, jornalTareo);
  }

  return partesDesdePesos(oficial, ctx.ingresosMensual, ctx.ingresosJornal);
}

function totalesDe(conceptos) {
  const totalIngresos = redondear2(
    (conceptos.ingresos || []).reduce((s, c) => s + (Number(c.monto) || 0), 0)
  );
  const totalDescuentos = redondear2(
    (conceptos.descuentos || []).reduce((s, c) => s + (Number(c.monto) || 0), 0)
  );
  const totalAportes = redondear2(
    (conceptos.aportes || []).reduce((s, c) => s + (Number(c.monto) || 0), 0)
  );
  return {
    totalIngresos,
    totalDescuentos,
    totalAportes,
    netoAPagar: redondear2(totalIngresos - totalDescuentos),
  };
}

function resumenTareosParte(empleado, tareosParte) {
  return {
    ...(empleado.resumenTareos || {}),
    total_tareos: tareosParte.length,
    dias_trabajados: tareosParte.filter((t) => {
      const tipo = normalizarTipoEvento(t.tipo_evento);
      return ['asistio', 'asistencia_completa', 'tardanza'].includes(tipo);
    }).length,
  };
}

function construirParte(empleado, tramo, conceptos, ctx) {
  const rango = tramo === 'jornal' ? ctx.rangoJornal : ctx.rangoMensual;
  const tareosParte = tareosEnRango(ctx.tareos, rango);
  const horas = tareosParte.reduce((s, t) => s + (Number(t.horas_trabajadas || t.horas) || 0), 0);
  const etiqueta = tramo === 'jornal' ? 'PARTE JORNAL' : 'PARTE MENSUAL';
  const dias = tramo === 'jornal'
    ? Number(ctx.meta.diasPagables) || 0
    : Number(ctx.meta.diasMensual) || 0;
  return {
    ...empleado,
    tramoMixto: tramo,
    rangoMixto: rango,
    subtituloBoleta: etiqueta,
    conceptos,
    totales: {
      ...(empleado.totales || {}),
      ...totalesDe(conceptos),
    },
    resumenTareos: resumenTareosParte(empleado, tareosParte),
    tareos: {
      ...(empleado.tareos || {}),
      horasOrdinarias: redondear2(horas),
    },
    pagoJornal: {
      ...(empleado.pagoJornal || {}),
      aclaracion: `${etiqueta}: ${dias} día${dias === 1 ? '' : 's'} del ${rango.inicio} al ${rango.fin}.`,
    },
  };
}

export function planillaParaBoletaMixto(planilla, empleadoParte) {
  const rango = empleadoParte?.rangoMixto;
  if (!rango?.inicio || !rango?.fin) return planilla;
  return {
    ...(planilla || {}),
    fecha_inicio: rango.inicio,
    fecha_fin: rango.fin,
  };
}

/**
 * Parte la boleta ya calculada en mensual + jornal solo para PDF/impresión.
 * No muta el empleado original ni el cálculo oficial.
 */
export function partirEmpleadoMixtoParaPdf(empleado) {
  if (!esMixtoParaBoletaPdf(empleado)) return [empleado];

  const meta = empleado.pagoJornal || {};
  const tareos = tareosDeEmpleado(empleado);
  const ingresosOficiales = listarConceptosGrupo(empleado, 'ingresos');
  const descuentosOficiales = listarConceptosGrupo(empleado, 'descuentos');
  const aportesOficiales = listarConceptosGrupo(empleado, 'aportes');

  const c0121 = ingresosOficiales.find((c) => codigoConceptoBoleta(c) === '0121');
  const split0121 = partir0121Mixto(meta, c0121?.monto || 0);
  const oficial0121 = redondear2(c0121?.monto || 0);
  const pesoMensual = oficial0121 > 0 ? split0121.mensual : (Number(meta.diasMensual) || 0);
  const pesoJornal = oficial0121 > 0 ? split0121.jornal : (Number(meta.diasPagables) || 0);

  const ctx = {
    meta,
    tareos,
    rangoMensual: meta.rangoMensual,
    rangoJornal: meta.rangoJornal,
    pesoMensual,
    pesoJornal,
    ratioMensual0121: split0121.mensual,
    horasTardanzaMensual: horasDeficitTardanzaEnRango(tareos, meta.rangoMensual),
    horasTardanzaJornal: horasDeficitTardanzaEnRango(tareos, meta.rangoJornal),
    ingresosMensual: 0,
    ingresosJornal: 0,
  };

  const ingresosMensual = [];
  const ingresosJornal = [];
  ingresosOficiales.forEach((concepto) => {
    const partes = normalizarPartes(
      codigoConceptoBoleta(concepto) === '0121' ? split0121 : partirIngreso(concepto, ctx),
      montoConcepto(concepto),
      ctx
    );
    if (partes.mensual > 0) ingresosMensual.push(clonarConcepto(concepto, partes.mensual));
    if (partes.jornal > 0) ingresosJornal.push(clonarConcepto(concepto, partes.jornal));
  });

  ctx.ingresosMensual = ingresosMensual.reduce((s, c) => s + (Number(c.monto) || 0), 0);
  ctx.ingresosJornal = ingresosJornal.reduce((s, c) => s + (Number(c.monto) || 0), 0);
  if (ctx.ingresosMensual <= 0 && ctx.ingresosJornal <= 0) {
    ctx.ingresosMensual = pesoMensual || 1;
    ctx.ingresosJornal = pesoJornal || 1;
  }

  const descuentosMensual = [];
  const descuentosJornal = [];
  descuentosOficiales.forEach((concepto) => {
    const partes = normalizarPartes(partirDescuento(concepto, ctx), montoConcepto(concepto), ctx);
    if (partes.mensual > 0) descuentosMensual.push(clonarConcepto(concepto, partes.mensual));
    if (partes.jornal > 0) descuentosJornal.push(clonarConcepto(concepto, partes.jornal));
  });

  const aportesMensual = [];
  const aportesJornal = [];
  aportesOficiales.forEach((concepto) => {
    const partes = normalizarPartes(
      partesDesdePesos(montoConcepto(concepto), ctx.ingresosMensual, ctx.ingresosJornal),
      montoConcepto(concepto),
      ctx
    );
    if (partes.mensual > 0) aportesMensual.push(clonarConcepto(concepto, partes.mensual));
    if (partes.jornal > 0) aportesJornal.push(clonarConcepto(concepto, partes.jornal));
  });

  const boletaCompleta = {
    ...empleado,
    tramoMixto: 'completo',
    subtituloBoleta: 'BOLETA COMPLETA',
    pagoJornal: {
      ...(empleado.pagoJornal || {}),
      aclaracion: empleado.pagoJornal?.aclaracion || 'Boleta completa del período (mensual + jornal).',
    },
  };

  return [
    boletaCompleta,
    construirParte(empleado, 'mensual', {
      ...(empleado.conceptos || {}),
      ingresos: ingresosMensual,
      descuentos: descuentosMensual,
      aportes: aportesMensual,
    }, ctx),
    construirParte(empleado, 'jornal', {
      ...(empleado.conceptos || {}),
      ingresos: ingresosJornal,
      descuentos: descuentosJornal,
      aportes: aportesJornal,
    }, ctx),
  ];
}
